import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { isChunkLoadError, reloadForNewBuild } from '@/lib/staleBuild';

interface Props {
	children: ReactNode;
}

interface State {
	hasError: boolean;
	/** Part of the app could not be loaded: most likely a newer deployment. */
	staleBuild: boolean;
}

/**
 * Top-level safety net. Renders a recoverable fallback instead of a blank
 * screen when a render error escapes. Replace with a monitoring report in prod.
 */
export class AppErrorBoundary extends Component<Props, State> {
	state: State = { hasError: false, staleBuild: false };

	static getDerivedStateFromError(error: unknown): State {
		return { hasError: true, staleBuild: isChunkLoadError(error) };
	}

	componentDidCatch(error: Error, info: ErrorInfo): void {
		// Re-rendering would request the same missing chunk again.
		if (isChunkLoadError(error) && reloadForNewBuild()) return;
		console.error('Unhandled render error', error, info.componentStack);
	}

	private readonly handleReset = () => {
		this.setState({ hasError: false, staleBuild: false });
	};

	private readonly handleReload = () => {
		window.location.reload();
	};

	render(): ReactNode {
		if (this.state.hasError && this.state.staleBuild) {
			return (
				<div className="flex min-h-screen items-center justify-center p-6">
					<div className="max-w-md text-center">
						<h1 className="text-2xl font-semibold tracking-tight">
							A new version is available
						</h1>
						<p className="text-muted-foreground mt-2 text-sm">
							The app was updated while this page was open. Reload to continue
							with the latest version.
						</p>
						<Button className="mt-6" onClick={this.handleReload}>
							Reload page
						</Button>
					</div>
				</div>
			);
		}

		if (this.state.hasError) {
			return (
				<div className="flex min-h-screen items-center justify-center p-6">
					<div className="max-w-md text-center">
						<h1 className="text-2xl font-semibold tracking-tight">
							Something went wrong
						</h1>
						<p className="text-muted-foreground mt-2 text-sm">
							An unexpected error occurred. Please try again.
						</p>
						<Button className="mt-6" onClick={this.handleReset}>
							Try again
						</Button>
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}
